import {
  Anchor,
  Button,
  Checkbox,
  Group,
  List,
  PasswordInput,
  Stack,
  Tabs,
  TextInput,
  ThemeIcon,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconCircleCheck,
  IconMessageCircle,
  IconPhoto,
} from "@tabler/icons-react";

import { useNavigate } from "react-router-dom";
// Import AuthService from its module (update the path as needed)
import { login } from "api/auth.service";
import { getCart } from "api/cart.service";

import { safeApiCall } from "api/common.service";
import { logError } from "api/diagnostic.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
} from "common/constants";
import { useCartStore } from "stores/useCartStore";
import { useSessionStore } from "stores/userSessionStore";
import { User, UserRole } from "types/interface.types";

// Define the props for the Login component
// This interface defines the structure of the props that the Login component will receive
interface LoginProps {
  values: {
    username: string;
    password: string;
    rememberMe: boolean;
  };
  setValues: (values: Partial<LoginProps["values"]>) => void;
  onSubmit: () => void;
  onForgotPassword: () => void;
}

// The Login component is a functional component that renders a login form with tabs for signing in and requesting a business account
// It uses Mantine components for styling and layout, and it handles login logic using the AuthService
// The component accepts props for managing form values, handling submission, and handling forgot password actions
export const Login = ({
  values,
  setValues,
  onSubmit,
  onForgotPassword,
}: LoginProps) => {
  const { setCart } = useCartStore();
  const navigate = useNavigate();
  // Inside your component
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const response = await safeApiCall(
        login(values.username, values.password),
      );
      if (response.user) {
        const user: User = response.user;
        useSessionStore.getState().setUser(user);
        if (!response.user || Object.keys(response.user).length === 0) {
          // Handle invalid login
          throw new Error(response.message);
        }

        notifications.show({
          title: `Welcome back, [${response.user.email}]!`,
          message: "We're thrilled to have you in our online community! 🌟",
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Success,
        });

        // clear the form
        setValues({ username: "", password: "", rememberMe: false });

        // navigate to default landing page
        // if user's role is admin or staff, take them to back order page instead of public web
        if (
          user?.role === UserRole.Customer ||
          user?.role === UserRole.Anonymous
        ) {
          navigate("/");
        } else {
          navigate("/admin/enquire-debtors");
        }

        // load stored cart items
        try {
          const cartResponse = await safeApiCall(getCart());
          if (cartResponse) {
            setCart(cartResponse.data ?? []);
          }
        } catch (error) {
          logError(error);
          notifications.show({
            title: "Cart Error",
            message: `Failed to load cart items.  ${error}`,
            position: MantineNotificationPosition.TopCenter,
            color: MantineNotificationColor.Error,
          });
        }

        // propagate the login event with the onSubmit callback so that the parent component can handle it.
        onSubmit();
      } else {
        // If the response does not contain a user, set the user to null
        useSessionStore.getState().setUser(null);

        notifications.show({
          title: "Login Failed!",
          message: `${response.message}`,
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Warn,
        });
      }
    } catch (error: any) {
      // Handle the error case
      // Log the error and set the user to null
      useSessionStore.getState().setUser(null);

      notifications.show({
        title: "Login Failed!",
        message: `Failed to signin. ${error.message}`,
        position: MantineNotificationPosition.TopCenter,
        color: MantineNotificationColor.Error,
      });
    }
  };
  // Function to handle the forgot password submission
  // This will send a reset password email to the provided email address
  return (
    <>
      <Tabs defaultValue="Sign In" mb="xl">
        <Tabs.List>
          <Tabs.Tab value="Sign In" leftSection={<IconPhoto size={12} />}>
            Sign In
          </Tabs.Tab>
          <Tabs.Tab
            value="Request Business Account"
            leftSection={<IconMessageCircle size={12} />}
          >
            Request Business Account
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="Sign In">
          <form onSubmit={handleLogin}>
            <Stack>
              <TextInput
                label="Username"
                placeholder="you@example.com"
                value={values.username}
                onChange={(e) => setValues({ username: e.currentTarget.value })}
                required
              />
              <PasswordInput
                label="Password"
                placeholder="••••••••"
                value={values.password}
                onChange={(e) => setValues({ password: e.currentTarget.value })}
                required
              />
              <Group justify="space-between" align="center">
                <Checkbox
                  label="Remember me"
                  checked={values.rememberMe}
                  onChange={(e) =>
                    setValues({ rememberMe: e.currentTarget.checked })
                  }
                />

                <Anchor
                  component="a"
                  size="sm"
                  onClick={onForgotPassword}
                  role="link"
                  tabIndex={0}
                >
                  Forgot password?
                </Anchor>
              </Group>
              <Button type="submit" fullWidth>
                Login
              </Button>
            </Stack>
          </form>
        </Tabs.Panel>

        <Tabs.Panel value="Request Business Account">
          <h3>Don’t have a business account?</h3>
          <p>The benefits of a JJL business account:</p>
          <List
            spacing="xs"
            size="sm"
            center
            icon={
              <ThemeIcon color="teal" size={24} radius="xl">
                <IconCircleCheck size={16} />
              </ThemeIcon>
            }
          >
            <List.Item>Create and/or track orders</List.Item>
            <List.Item>Review past order(s) </List.Item>
            <List.Item>No extra costs or conditions</List.Item>
          </List>
          <p>Want to know more, get in touch with us on +64 4 569 5248</p>
        </Tabs.Panel>

        <Tabs.Panel value="settings">Settings tab content</Tabs.Panel>
      </Tabs>
    </>
  );
};
